import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../services/api';
import api from '../utils/apiInterceptor';

// AI 通道说明：辅助创作 100% 走 DeepSeek 逆向号池（无官方通道），模型由号池统一提供

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.white,
  borderBottom: '1px solid rgba(0, 255, 159, 0.1)',
  padding: '8px 12px',
  fontSize: 13,
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 30, 60, 0.5)' },
  '&:nth-of-type(even)': { backgroundColor: 'rgba(0, 20, 40, 0.5)' },
  '&:hover': { backgroundColor: 'rgba(0, 255, 159, 0.08) !important' },
  '&:last-child td, &:last-child th': { borderBottom: 0 },
}));

const QuotaBar = ({ used, limit }) => {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const color = pct > 80 ? '#ff6b6b' : pct > 50 ? '#ffd700' : '#00ff9f';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={100 - pct}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 60 }}>
        {limit - used}/{limit}
      </Typography>
    </Box>
  );
};

const AssistAiQuotaManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState(null);

  // 每日限额配置
  const [dailyLimitConfig, setDailyLimitConfig] = useState({ dailyLimit: 50, defaultLimit: 50, configSource: 'default' });
  const [limitInputValue, setLimitInputValue] = useState(50);
  const [limitLoading, setLimitLoading] = useState(false);

  // 号池通道状态（辅助创作 100% 走逆向号池）
  const [poolStatus, setPoolStatus] = useState(null);

  // 修改次数弹窗
  const [editDialog, setEditDialog] = useState({ open: false, user: null, usedToday: 0 });
  // 扣费记录弹窗
  const [logDialog, setLogDialog] = useState({ open: false, user: null, logs: [], loading: false, total: 0 });

  // 加载每日限额配置
  const loadDailyLimitConfig = useCallback(async () => {
    try {
      const res = await apiService.getAssistAiDailyLimitConfig();
      if (res.success && res.data) {
        setDailyLimitConfig(res.data);
        setLimitInputValue(res.data.dailyLimit);
      }
    } catch (e) {
      console.error('加载每日限额配置失败', e);
    }
  }, []);

  // 保存每日限额
  const saveDailyLimit = async () => {
    const newLimit = parseInt(limitInputValue, 10);
    if (isNaN(newLimit) || newLimit < 1) {
      setSnackbar({ open: true, message: '每日限额必须是大于0的整数', severity: 'error' });
      return;
    }
    setLimitLoading(true);
    try {
      const res = await apiService.setAssistAiDailyLimit(newLimit);
      if (res.success) {
        setDailyLimitConfig((prev) => ({ ...prev, dailyLimit: newLimit, configSource: 'database' }));
        setSnackbar({ open: true, message: `每日限额已设置为 ${newLimit} 次`, severity: 'success' });
        loadData(); // 刷新数据以更新显示
      } else {
        setSnackbar({ open: true, message: res.message || '设置失败', severity: 'error' });
      }
    } catch (e) {
      console.error('保存每日限额失败', e);
      setSnackbar({ open: true, message: '设置失败: ' + e.message, severity: 'error' });
    } finally {
      setLimitLoading(false);
    }
  };

  useEffect(() => {
    loadDailyLimitConfig();
  }, [loadDailyLimitConfig]);

  // 加载号池通道状态
  const loadPoolStatus = useCallback(async () => {
    try {
      const res = await api.get('/assist/ai/pool-status');
      const data = res.data || res;
      if (data.success) {
        setPoolStatus(data);
      }
    } catch (e) {
      console.error('加载号池状态失败', e);
    }
  }, []);

  useEffect(() => {
    loadPoolStatus();
  }, [loadPoolStatus]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [data, statsData] = await Promise.all([
        apiService.getAssistAiQuotaList({ page: page + 1, limit: rowsPerPage, search }),
        apiService.getAssistAiStats(),
      ]);
      if (data.success) {
        setRows(data.data);
        setTotal(data.total);
      }
      if (statsData.success) setStats(statsData.data);
    } catch (e) {
      console.error('加载失败', e);
      setSnackbar({ open: true, message: '加载数据失败', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = () => { setPage(0); loadData(); };

  const openEditDialog = (user) => {
    const today = new Date().toISOString().slice(0, 10);
    const isToday = user.assistAiLastUsedDate === today;
    setEditDialog({ open: true, user, usedToday: isToday ? user.assistAiDailyUsed : 0 });
  };

  const confirmEdit = async () => {
    try {
      const res = await apiService.updateAssistAiQuota(editDialog.user._id, editDialog.usedToday);
      if (res.success) {
        setSnackbar({ open: true, message: res.message, severity: 'success' });
        setEditDialog({ open: false, user: null, usedToday: 0 });
        loadData();
      } else {
        setSnackbar({ open: true, message: res.message || '修改失败', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || '修改失败', severity: 'error' });
    }
  };

  const handleReset = async (user) => {
    try {
      const res = await apiService.resetAssistAiQuota(user._id);
      if (res.success) {
        setSnackbar({ open: true, message: res.message, severity: 'success' });
        loadData();
      } else {
        setSnackbar({ open: true, message: res.message || '重置失败', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || '重置失败', severity: 'error' });
    }
  };

  const openLogDialog = async (user) => {
    setLogDialog({ open: true, user, logs: [], loading: true, total: 0 });
    try {
      const res = await apiService.getAssistAiUsageLogs(user._id, { page: 1, limit: 30 });
      if (res.success) {
        setLogDialog((prev) => ({ ...prev, logs: res.data, total: res.total, loading: false }));
      } else {
        setLogDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch (e) {
      setLogDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Box>
      {/* AI 通道状态卡片（纯逆向号池） */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(0,30,60,0.6)', border: '1px solid rgba(0,255,159,0.2)' }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon sx={{ color: '#00e5c4', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ color: '#00e5c4', fontWeight: 700 }}>
                AI 通道：DeepSeek 逆向号池
              </Typography>
            </Box>
            <Chip
              label={
                poolStatus == null ? '状态加载中...'
                  : !poolStatus.enabled ? '通道未启用'
                  : poolStatus.assistAvailable
                    ? `空闲 ${poolStatus.schedulable} 个 · 可用`
                    : `繁忙 · 排队让号中（空闲 ${poolStatus.schedulable}）`
              }
              sx={{
                bgcolor: poolStatus?.assistAvailable ? 'rgba(0,229,196,0.15)' : 'rgba(255,193,7,0.15)',
                color: poolStatus?.assistAvailable ? '#00e5c4' : '#ffc107',
                fontWeight: 600,
                border: `1px solid ${poolStatus?.assistAvailable ? 'rgba(0,229,196,0.3)' : 'rgba(255,193,7,0.3)'}`
              }}
            />
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadPoolStatus}
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
              刷新状态
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
            辅助创作 100% 走逆向号池（专属通道，不消耗官方额度）；号池繁忙时自动排队让号——保留 {poolStatus?.reserve ?? 5} 个空闲号优先供收费的生成任务使用
          </Typography>
        </CardContent>
      </Card>

      {/* 每日免费次数限额配置卡片 */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(0,30,60,0.6)', border: '1px solid rgba(0,255,159,0.2)' }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon sx={{ color: '#00e5c4', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ color: '#00e5c4', fontWeight: 700 }}>
                每日免费次数限额
              </Typography>
            </Box>
            <TextField
              size="small"
              type="number"
              value={limitInputValue}
              onChange={(e) => setLimitInputValue(Math.max(1, parseInt(e.target.value, 10) || 1))}
              inputProps={{ min: 1, max: 9999 }}
              disabled={limitLoading}
              sx={{
                width: 100,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(0,255,159,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,255,159,0.5)' },
                },
                '& .MuiInputBase-input': { textAlign: 'center' }
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={saveDailyLimit}
              disabled={limitLoading}
              startIcon={limitLoading ? <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} /> : <SaveIcon />}
              sx={{
                bgcolor: 'rgba(0,255,159,0.15)',
                color: '#00ff9f',
                border: '1px solid rgba(0,255,159,0.3)',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(0,255,159,0.25)' }
              }}
            >
              {limitLoading ? '保存中...' : '保存设置'}
            </Button>
            <Chip
              label={`当前限额: ${dailyLimitConfig.dailyLimit} 次/天`}
              sx={{
                bgcolor: 'rgba(0,229,196,0.15)',
                color: '#00e5c4',
                fontWeight: 600,
                border: '1px solid rgba(0,229,196,0.3)'
              }}
            />
            <Chip
              label={dailyLimitConfig.configSource === 'database' ? '已自定义' : '默认值'}
              size="small"
              sx={{
                bgcolor: dailyLimitConfig.configSource === 'database' ? 'rgba(76,175,80,0.15)' : 'rgba(255,193,7,0.15)',
                color: dailyLimitConfig.configSource === 'database' ? '#4caf50' : '#ffc107',
                fontWeight: 600
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
            💡 设置用户每天可免费使用辅助创作的次数，到达限额后需等到次日0点自动重置。默认值: {dailyLimitConfig.defaultLimit} 次
          </Typography>
        </CardContent>
      </Card>

      {/* 统计卡片 */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          {[
            { label: '注册用户', value: stats.totalUsers },
            { label: '今日已使用', value: stats.usedTodayUsers },
            { label: '今日调用总次数', value: stats.totalCallsToday },
            { label: '每日限额', value: stats.dailyLimit + ' 次' },
          ].map(({ label, value }) => (
            <Paper key={label} sx={{ p: 2, border: '1px solid rgba(0,255,159,0.2)', borderRadius: 2, textAlign: 'center', background: 'rgba(0,30,60,0.6)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{label}</Typography>
              <Typography variant="h5" sx={{ color: '#00ff9f', fontWeight: 800 }}>{value ?? 0}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* 搜索栏 */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="搜索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 }, maxWidth: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(0,255,159,0.2)' } } }}
        />
        <Button variant="outlined" size="small" onClick={handleSearch} sx={{ color: '#00ff9f', borderColor: '#00ff9f', '&:hover': { borderColor: '#00ff9f', bgcolor: 'rgba(0,255,159,0.08)' } }}>
          搜索
        </Button>
        <Button variant="text" size="small" startIcon={<RefreshIcon />} onClick={loadData} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          刷新
        </Button>
      </Box>

      {/* 表格 */}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(0,10,20,0.6)', borderRadius: 1, border: '1px solid rgba(0,255,159,0.1)', overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <StyledTableRow sx={{ bgcolor: 'rgba(0,30,60,0.8)' }}>
              {['用户名', '手机', '邮箱', '注册时间', '今日已用/剩余', '用量进度', '操作'].map((h) => (
                <StyledTableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</StyledTableCell>
              ))}
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={24} sx={{ color: '#00ff9f' }} />
                </StyledTableCell>
              </StyledTableRow>
            ) : rows.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 6, color: 'rgba(255,255,255,0.4)' }}>
                  暂无数据
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              rows.map((r) => (
                <StyledTableRow key={r._id}>
                  <StyledTableCell>
                    <Typography sx={{ color: '#90caf9', fontWeight: 600 }}>{r.username}</Typography>
                  </StyledTableCell>
                  <StyledTableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{r.phone}</StyledTableCell>
                  <StyledTableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{r.email}</StyledTableCell>
                  <StyledTableCell sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString('zh-CN') : '—'}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={`已用 ${r.assistAiDailyUsed} 次`}
                      size="small"
                      sx={{
                        bgcolor: r.remaining === 0 ? 'rgba(255,107,107,0.2)' : r.remaining < 10 ? 'rgba(255,215,0,0.15)' : 'rgba(0,255,159,0.12)',
                        color: r.remaining === 0 ? '#ff6b6b' : r.remaining < 10 ? '#ffd700' : '#00ff9f',
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    />
                    {r.isReset && (
                      <Chip label="已重置" size="small" sx={{ ml: 0.5, bgcolor: 'rgba(100,181,246,0.15)', color: '#90caf9', fontSize: 10 }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <QuotaBar used={r.assistAiDailyUsed} limit={r.dailyLimit} />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="查看扣费记录">
                        <IconButton size="small" onClick={() => openLogDialog(r)} sx={{ color: '#90caf9' }}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="修改次数">
                        <IconButton size="small" onClick={() => openEditDialog(r)} sx={{ color: '#ffd700' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="重置今日次数">
                        <IconButton size="small" onClick={() => handleReset(r)} sx={{ color: '#ff6b6b' }}>
                          <RestartAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
        sx={{ color: 'rgba(255,255,255,0.6)', '& .MuiTablePagination-select': { color: 'white' } }}
      />

      {/* 修改次数弹窗 */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, user: null, usedToday: 0 })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#ffd700', fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          修改 {editDialog.user?.username} 今日已用次数
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
            当前已用：{editDialog.usedToday} 次（每日限额 {dailyLimitConfig.dailyLimit} 次）
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="今日已用次数"
            type="number"
            value={editDialog.usedToday}
            onChange={(e) => setEditDialog((d) => ({ ...d, usedToday: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
            inputProps={{ min: 0, max: dailyLimitConfig.dailyLimit }}
            sx={{ '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,215,0,0.4)' } }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}
            helperText={`修改后剩余次数 = ${dailyLimitConfig.dailyLimit - editDialog.usedToday} 次`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button onClick={() => setEditDialog({ open: false, user: null, usedToday: 0 })} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            取消
          </Button>
          <Button onClick={confirmEdit} variant="contained" sx={{ bgcolor: '#ffd700', color: '#001e3c', fontWeight: 700, '&:hover': { bgcolor: '#ffca28' } }}>
            确认修改
          </Button>
        </DialogActions>
      </Dialog>

      {/* 扣费记录弹窗 */}
      <Dialog open={logDialog.open} onClose={() => setLogDialog({ open: false, user: null, logs: [], loading: false, total: 0 })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#00ff9f', fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {logDialog.user?.username} — 扣费记录
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {logDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: '#00ff9f' }} /></Box>
          ) : logDialog.logs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.4)' }}>暂无扣费记录</Box>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {logDialog.logs.map((log) => {
                // 从 metadata 中提取详细信息
                const meta = log.metadata || {};
                const novelName = meta.novelName || (log.description ? log.description.match(/【(.+?)】/)?.[1] : '');
                const chapterInfo = meta.chapterNumber ? `第${meta.chapterNumber}章` : (log.description ? log.description.match(/第(\d+)章/)?.[0] : '');
                const operationType = meta.operationType || '';

                // 根据记录类型显示不同标签
                const logTypeLabel = {
                  'consumption': '扣费',
                  'recharge': '充值',
                  'admin': '管理员操作',
                  'free': '免费'
                }[log.type] || '扣费';

                const logTypeColor = {
                  'consumption': '#ff6b6b',
                  'recharge': '#00ff9f',
                  'admin': '#90caf9',
                  'free': '#ffd700'
                }[log.type] || '#ff6b6b';

                return (
                <Box key={log._id} sx={{ px: 2, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.3 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                      {log.description || '辅助创作 AI 调用'}
                    </Typography>
                    <Chip
                      label={logTypeLabel}
                      size="small"
                      sx={{ bgcolor: `${logTypeColor}22`, color: logTypeColor, fontWeight: 600, fontSize: 10, height: 18 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    {novelName && (
                      <Typography variant="caption" sx={{ color: 'rgba(34, 211, 238, 0.85)', fontWeight: 600 }}>
                        [项目] {novelName}
                      </Typography>
                    )}
                    {chapterInfo && (
                      <Typography variant="caption" sx={{ color: 'rgba(251, 191, 36, 0.9)', fontWeight: 600 }}>
                        [章节] {chapterInfo}
                      </Typography>
                    )}
                    {operationType && (
                      <Typography variant="caption" sx={{ color: 'rgba(167, 139, 250, 0.85)', fontWeight: 500 }}>
                        [操作] {operationType}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: '#ff6b6b', fontWeight: 700 }}>
                      扣减 {log.delta != null ? Math.abs(log.delta) : 1} 次
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#ffd700', fontWeight: 700 }}>
                      剩余 {log.balanceAfter != null ? log.balanceAfter : '—'} 次
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', ml: 'auto' }}>
                      {log.createdAt ? new Date(log.createdAt).toLocaleString('zh-CN') : ''}
                    </Typography>
                  </Box>
                </Box>
              )})}
            </Box>
          )}
          {!logDialog.loading && logDialog.total > 0 && (
            <Typography variant="caption" sx={{ display: 'block', px: 2, pb: 1.5, color: 'rgba(255,255,255,0.35)' }}>
              共 {logDialog.total} 条记录
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setLogDialog({ open: false, user: null, logs: [], loading: false, total: 0 })} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            关闭
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ bgcolor: snackbar.severity === 'error' ? 'rgba(255,80,80,0.9)' : 'rgba(0,180,80,0.9)', color: 'white' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssistAiQuotaManagement;
